import axios, { type AxiosProgressEvent } from "axios";
import type { ChapterImageQualities } from "~/src/api";

// const GlobalPagesLoading = useState('reader-pages-loading', () => 0)

export class ImagePageLoader {
  serverSrc: string;
  width: number;
  height: number;
  blob?: Blob;
  expiresAt?: number;
  error?: any;
  quality?: ChapterImageQualities;
  loading: boolean = false;
  imagePromise?: Promise<Blob>;
  progress = 0;
  abortController?: AbortController;
  blobUrl?: string;

  constructor(options: {
    src: string;
    width: number;
    height: number;
    expiresAt?: number;
    file?: Blob;
    quality?: ChapterImageQualities;
  }) {
    this.width = options.width;
    this.height = options.height;
    this.serverSrc = options.src;
    this.expiresAt = options.expiresAt;
    this.quality = options.quality;

    if (options.file) {
      this.progress = 1;
      this.blob = options.file;
      this.loading = false;
    }
  }
  async load(force = false) {
    if (this.isLoaded() && !force) return;

    const blob = await this.attemptImageDownload(force);
    this.destroyAfterExpire();

    return blob;
  }

  unload() {
    this.destroyBlob();
  }

  isLoaded() {
    return this.blob !== undefined && !this.error;
  }

  hasExpired() {
    return this.expiresAt && this.expiresAt < Date.now();
  }

  private async attemptImageDownload(force = false) {
    if (this.imagePromise && !force) return this.imagePromise;

    try {
      this.loading = true;
      this.error = undefined;
      const blob = await this.getImageData(force);
      this.blob = blob;
      if (this.blobUrl) URL.revokeObjectURL(this.blobUrl);
      this.blobUrl = URL.createObjectURL(blob);
      return blob;
    } catch (e) {
      this.error = e;
    } finally {
      this.loading = false;
    }
  }

  private async getImageData(force = false): Promise<Blob> {
    if (this.blob && !force) return this.blob;

    this.progress = 0;
    this.abortController = new AbortController();
    this.imagePromise = new Promise<Blob>(
      async (resolve, reject) =>
        await axios
          .get(this.serverSrc, {
            onDownloadProgress: (event) => this.updateProgress(event),
            responseType: "blob",
            signal: this.abortController!.signal,
          })
          .then((res) => {
            resolve(res.data);
            this.imagePromise = undefined;
          })
          .catch(reject),
    );

    return this.imagePromise;
  }

  private updateProgress(event: AxiosProgressEvent) {
    this.progress = event.progress ?? 0;
  }

  private destroyAfterExpire() {
    if (!this.expiresAt) return;
    const timeToDestroy = this.expiresAt - Date.now();
    setTimeout(() => this.destroyBlob(), Math.min(0, timeToDestroy));
  }

  private destroyBlob() {
    delete this.blob;
    if (this.blobUrl) {
      URL.revokeObjectURL(this.blobUrl);
      this.blobUrl = undefined;
    }
    this.progress = 0;
  }

  public destroy() {
    this.abortController?.abort();
    this.destroyBlob();
  }
}
